import { type CSSProperties, type FC, type ReactNode, useEffect } from 'react';

import type {
  PannellumConfig,
  PannellumEquirectangularOptions,
  PannellumHotSpot,
  PannellumMultiRes,
  PannellumViewer
} from './pannellum.types';

export type {
  PannellumConfig,
  PannellumEquirectangularOptions,
  PannellumHotSpot,
  PannellumMultiRes,
  PannellumViewer
};

// ---------------------------------------------------------------------------
// Module-level viewer registry (mirrors react-pannellum behaviour)
// ---------------------------------------------------------------------------

const viewers: Record<string, PannellumViewer> = {};

let loadPromise: Promise<void> | null = null;

function loadPannellum(): Promise<void> {
  if (typeof window !== 'undefined' && window.pannellum) {
    return Promise.resolve();
  }
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    if (!document.querySelector('link[href="/pannellum/pannellum.css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = '/pannellum/pannellum.css';
      document.head.appendChild(link);
    }

    const script = document.createElement('script');
    script.src = '/pannellum/pannellum.js';
    script.onload = () => resolve();
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Failed to load Pannellum'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

// ---------------------------------------------------------------------------
// Static API — mirrors the named exports of react-pannellum
// ---------------------------------------------------------------------------

export function getViewer(id?: string): PannellumViewer | undefined {
  if (id !== undefined && viewers[id]) return viewers[id];
  const ids = Object.keys(viewers);
  return ids.length > 0 ? viewers[ids[ids.length - 1]] : undefined;
}

export function getViewers(): Record<string, PannellumViewer> {
  return viewers;
}

export function isLoaded(): boolean {
  return getViewer()?.isLoaded() ?? false;
}

export function getConfig(): PannellumConfig {
  return getViewer()?.getConfig() ?? {};
}

export function getContainer(): HTMLElement | undefined {
  return getViewer()?.getContainer();
}

export function getPitch(): number {
  return getViewer()?.getPitch() ?? 0;
}

export function setPitch(pitch: number, animated?: number): void {
  getViewer()?.setPitch(pitch, animated);
}

export function getPitchBounds(): [number, number] {
  return getViewer()?.getPitchBounds() ?? [-90, 90];
}

export function setPitchBounds(bounds: [number, number]): void {
  getViewer()?.setPitchBounds(bounds);
}

export function getYaw(): number {
  return getViewer()?.getYaw() ?? 0;
}

export function setYaw(yaw: number, animated?: number): void {
  getViewer()?.setYaw(yaw, animated);
}

export function getYawBounds(): [number, number] {
  return getViewer()?.getYawBounds() ?? [-180, 180];
}

export function setYawBounds(bounds: [number, number]): void {
  getViewer()?.setYawBounds(bounds);
}

export function getHfov(): number {
  return getViewer()?.getHfov() ?? 100;
}

export function setHfov(hfov: number, animated?: number): void {
  getViewer()?.setHfov(hfov, animated);
}

export function getHfovBounds(): [number, number] {
  return getViewer()?.getHfovBounds() ?? [50, 120];
}

export function setHfovBounds(bounds: [number, number]): void {
  getViewer()?.setHfovBounds(bounds);
}

export function lookAt(
  pitch?: number,
  yaw?: number,
  hfov?: number,
  animated?: number
): void {
  getViewer()?.lookAt(pitch, yaw, hfov, animated);
}

export function getNorthOffset(): number {
  return getViewer()?.getNorthOffset() ?? 0;
}

export function setNorthOffset(heading: number): void {
  getViewer()?.setNorthOffset(heading);
}

export function getHorizonRoll(): number {
  return getViewer()?.getHorizonRoll() ?? 0;
}

export function setHorizonRoll(roll: number): void {
  getViewer()?.setHorizonRoll(roll);
}

export function getHorizonPitch(): number {
  return getViewer()?.getHorizonPitch() ?? 0;
}

export function setHorizonPitch(pitch: number): void {
  getViewer()?.setHorizonPitch(pitch);
}

export function startAutoRotate(speed?: number, pitch?: number): void {
  getViewer()?.startAutoRotate(speed, pitch);
}

export function stopAutoRotate(): void {
  getViewer()?.stopAutoRotate();
}

export function getCurrentScene(): string {
  return getViewer()?.getScene() ?? '';
}

export function addScene(sceneId: string, config: PannellumConfig): void {
  getViewer()?.addScene(sceneId, config);
}

export function removeScene(sceneId: string): void {
  getViewer()?.removeScene(sceneId);
}

export function loadScene(
  sceneId: string,
  targetPitch?: number | 'same',
  targetYaw?: number | 'same' | 'sameAzimuth',
  targetHfov?: number | 'same'
): void {
  getViewer()?.loadScene(sceneId, targetPitch, targetYaw, targetHfov);
}

export function addHotSpot(hotspot: PannellumHotSpot, sceneId?: string): void {
  getViewer()?.addHotSpot(hotspot, sceneId);
}

export function removeHotSpot(hotSpotId: string, sceneId?: string): void {
  getViewer()?.removeHotSpot(hotSpotId, sceneId);
}

export function toggleFullscreen(): void {
  getViewer()?.toggleFullscreen();
}

export function mouseEventToCoords(event: MouseEvent): [number, number] {
  return getViewer()?.mouseEventToCoords(event) ?? [0, 0];
}

export function stopMovement(): void {
  getViewer()?.stopMovement();
}

export function resize(): void {
  getViewer()?.resize();
}

export function isOrientationSupported(): boolean {
  return getViewer()?.isOrientationSupported() ?? false;
}

export function startOrientation(): void {
  getViewer()?.startOrientation();
}

export function stopOrientation(): void {
  getViewer()?.stopOrientation();
}

export function isOrientationActive(): boolean {
  return getViewer()?.isOrientationActive() ?? false;
}

export function destroy(): void {
  getViewer()?.destroy();
}

// ---------------------------------------------------------------------------
// Component props
// ---------------------------------------------------------------------------

export interface ReactPannellumProps {
  id: string;
  sceneId: string;
  children?: ReactNode;
  type?: 'equirectangular' | 'cubemap' | 'multires';
  imageSource?: string;
  equirectangularOptions?: PannellumEquirectangularOptions;
  cubeMap?: string[];
  multiRes?: PannellumMultiRes;
  config?: PannellumConfig;
  className?: string;
  style?: CSSProperties;
  onPanoramaLoaded?: () => void;
  onPanoramaMouseUp?: (event: MouseEvent) => void;
  onPanoramaMouseDown?: (event: MouseEvent) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ReactPannellum: FC<ReactPannellumProps> = ({
  id,
  sceneId,
  children,
  type = 'equirectangular',
  imageSource = '',
  equirectangularOptions = {},
  cubeMap = [],
  multiRes = {},
  config = {},
  className = '',
  style = { width: '600px', height: '400px', background: '#000000' },
  onPanoramaLoaded,
  onPanoramaMouseUp,
  onPanoramaMouseDown
}) => {
  useEffect(() => {
    if (viewers[id]) {
      viewers[id].destroy();
      delete viewers[id];
    }

    let cancelled = false;

    loadPannellum().then(() => {
      if (cancelled) return;

      let sceneConfig: Record<string, unknown>;

      if (type === 'cubemap') {
        sceneConfig = { type: 'cubemap', cubeMap, ...config };
      } else if (type === 'multires') {
        sceneConfig = { type: 'multires', multiRes, ...config };
      } else {
        sceneConfig = {
          type: 'equirectangular',
          panorama: imageSource,
          ...equirectangularOptions,
          ...config
        };
      }

      const viewer = window.pannellum.viewer(id, {
        default: { firstScene: sceneId },
        scenes: { [sceneId]: sceneConfig }
      });

      viewers[id] = viewer;

      if (onPanoramaLoaded) viewer.on('load', onPanoramaLoaded);
      if (onPanoramaMouseDown) viewer.on('mousedown', onPanoramaMouseDown);
      if (onPanoramaMouseUp) viewer.on('mouseup', onPanoramaMouseUp);
    });

    return () => {
      cancelled = true;
      const viewer = viewers[id];
      if (viewer) {
        if (onPanoramaLoaded) viewer.off('load', onPanoramaLoaded);
        if (onPanoramaMouseDown) viewer.off('mousedown', onPanoramaMouseDown);
        if (onPanoramaMouseUp) viewer.off('mouseup', onPanoramaMouseUp);
      }
    };
  }, []);

  return (
    <div id={id} style={style} className={className}>
      {children}
    </div>
  );
};

export default ReactPannellum;
